import { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Button from "../../components/ui/button/Button";
import { createSession } from "../../lib/api/w3-wizard";
import JurisdictionBadge, { jurisdictionFromApiNumber } from "../../components/ui/JurisdictionBadge";

export default function W3WizardNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = (location.state as { api14?: string; jurisdiction?: string } | null) ?? {};
  const [apiNumber, setApiNumber] = useState(prefill.api14 ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiNumber.trim()) return;

    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      const session = await createSession(apiNumber.trim());
      if (!session.plan_snapshot) {
        navigate(`/w3-wizard/${session.id}?no_plan=1`);
      } else {
        navigate(`/w3-wizard/${session.id}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create session.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageMeta
        title="New Post Plugging Report | RegulAgent"
        description="Start a new post plugging report session"
      />
      <PageBreadcrumb pageTitle="New Post Plugging Report" />

      <div className="max-w-md mx-auto">
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Start Post Plugging Report
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Enter the API number for the well you want to generate a post plugging report for.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                API Number
              </label>
              <input
                type="text"
                value={apiNumber}
                onChange={(e) => setApiNumber(e.target.value)}
                placeholder="e.g. 30-015-12345-0000"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                autoFocus
              />
              {apiNumber.trim().length >= 2 && (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Detected:</span>
                  <JurisdictionBadge jurisdiction={jurisdictionFromApiNumber(apiNumber)} />
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-error-50 dark:bg-error-500/15 border border-error-200 dark:border-error-500/30 p-3">
                <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
              </div>
            )}

            {warning && (
              <div className="rounded-lg bg-warning-50 dark:bg-warning-500/15 border border-warning-200 dark:border-warning-500/30 p-3">
                <p className="text-sm text-warning-700 dark:text-warning-400">{warning}</p>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Button
                variant="outline"
                onClick={() => navigate("/w3-wizard")}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!apiNumber.trim() || loading}
                className="flex-1"
              >
                {loading ? "Creating..." : "Start Wizard"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
